import { IColumn, ITypeAlign } from './types';

// ==============================|| CUSTOM TABLE - SERVER-DRIVEN COLUMN CONFIG ||============================== //
// Some tables receive their column layout from the BE (GET <resource>/table-config).
// The server decides WHICH columns exist, their ORDER, LABELS, default VISIBILITY and
// SORTABILITY; the local column definitions keep everything the server cannot know:
// widths, sort-key mappings and how the page builds each cell (row keys / renderers).
// Merge result feeds CustomTable as usual — visibility integrates with the "Manage
// columns" menu through `defaultHidden` (a stored user preference always wins).

/** One column as described by the BE table-config endpoint. */
export interface IServerColumnConfig {
	key: string;
	label: string;
	visible: boolean;
	sortable: boolean;
	/** Semantic value type (informative — cells are rendered by the page). */
	type?: string | null;
	align?: string | null;
	order: number;
}

/** Response shape of GET <resource>/table-config. */
export interface IServerTableConfig {
	columns: IServerColumnConfig[];
}

const ALIGN_MAP: Record<string, ITypeAlign> = {
	left: 'left',
	right: 'right',
	center: 'center',
	centre: 'center'
};

const alignOf = (value?: string | null): ITypeAlign | undefined =>
	value ? ALIGN_MAP[value.toLowerCase()] : undefined;

/**
 * Merges the BE column configuration into the locally defined columns.
 *
 * - BE columns come out first, sorted by `order`; each one is matched by `key`
 *   (case-insensitive) with a local definition to inherit width / sortKey / align / the
 *   local key (so dedicated renderers and row data resolve regardless of BE key casing).
 * - BE keys unknown locally still render generically (raw row value) and keep the BE
 *   visibility as-is — including columns with no data — so mismatches surface in the table
 *   and in "Manage columns" for diagnosis, rather than being silently hidden by the FE.
 * - Local columns the BE does not mention (FE-only ones such as `actions`) are appended
 *   at the end, unchanged.
 * - `visible: false` becomes `defaultHidden` — the column starts hidden but stays
 *   available in the "Manage columns" menu.
 * - With no / empty config (endpoint not live yet or failed) the local definitions are
 *   returned untouched, so pages degrade silently.
 */
export const applyServerColumnConfig = (
	local: IColumn[],
	config?: IServerColumnConfig[] | null
): IColumn[] => {
	if (!config || config.length === 0) return local;
	// Potrivirea local↔remote se face pe cheie NORMALIZATĂ (case-insensitive): unele
	// config-uri BE trimit cheile cu alt casing decât DTO-ul/FE-ul (ex. „createdat" vs
	// „createdAt", „viewcount" vs „viewCount"). Fără normalizare, coloana BE nu se
	// potrivea cu cea locală → se dubla (una goală, din config; una cu date, FE-only).
	const norm = (key: string): string => key.toLowerCase();
	const byKey = new Map(local.map((col) => [norm(col.key), col]));
	const known = new Set(config.map((col) => norm(col.key)));

	const fromServer = [...config]
		.sort((a, b) => a.order - b.order)
		.map((remote): IColumn => {
			const base = byKey.get(norm(remote.key));
			return {
				// Când există potrivire locală, folosim cheia LOCALĂ (ex. camelCase), ca
				// randările dedicate (`custom[col.key]`) și datele (`raw[col.key]`) să se
				// rezolve corect chiar dacă BE trimite cheia cu alt casing.
				key: base?.key ?? remote.key,
				width: base?.width ?? '12%',
				minWidth: base?.minWidth,
				label: remote.label || base?.label || remote.key,
				align: alignOf(remote.align) ?? base?.align ?? 'left',
				sortable: remote.sortable,
				sortKey: base?.sortKey,
				// Vizibilitatea vine INTEGRAL din config-ul BE (inclusiv coloanele fără
				// corespondent local) — le păstrăm în tabel și în „Gestionează coloane" exact
				// cum le trimite BE, ca eventualele coloane goale să fie vizibile pentru
				// diagnostic și reparate la sursă (config BE / DTO), nu ascunse tacit de FE.
				defaultHidden: !remote.visible,
				// Tipul semantic din config → randare generică pe tip (renderTypedCell)
				// pentru coloanele fără renderer dedicat în pagină.
				type: remote.type ?? base?.type
			};
		});

	const feOnly = local.filter((col) => !known.has(norm(col.key)));
	return [...fromServer, ...feOnly];
};
